import { createApi } from '@reduxjs/toolkit/query/react';

// import { IS_PROD } from '@/config/env';
import { Address } from '@/types/Address';
import { ResponseError } from '@/types/Error';
import {
  AddToExistingEzShipPayload,
  AddToEzshipPayload,
  AddToEzShipResponse,
  CustomerContactDetailsPayload,
  CustomerShippingAddressDetailsPayload,
  EzshipAutoOrderDetailResponse,
  EzShipCartResp,
  EzshipManagerAutoOrderHistory,
  EzShipsResponse,
  EzShipType,
  SkipProductInAutoOrderPayload,
  UpdateEzshipDeliveryPreference,
  UpdateProductQuantityInAutoOrderPayload,
} from '@/types/EzShip';
import { AddCreditCardPayload } from '@/types/PaymentCard';
import { SuccessResponse } from '@/types/Success';

import { store } from '..';
import { getCustomerEzShipsAdapter } from '../adapters';
import { generateBaseQuseryWithReAuth } from '../baseQueryReauth';
import {
  removeAction,
  setCurrentEzShip,
  setEzShipConfig,
  setEzShipTitle,
  setFoodLabelRemovalStatus,
  setGetEzShipCartItemsStatus,
  setRemoveEzShipCartItemStatus,
} from '../slices/ezship';
import { addressApi } from './user/account/address';

const contentTypes = {
  applicationJson: 'application/json',
};

export const ezShipApi = createApi({
  reducerPath: 'ezShipApi',
  keepUnusedDataFor: 300,
  baseQuery: generateBaseQuseryWithReAuth({ basePath: `EZShip` }),
  tagTypes: [
    'CustomerEzShips',
    'GetCustomerAutoOrderList',
    'AllEzShips',
    'EzShipCartItems',
    'GetEzshipOrdersHistory',
    'UpdateEzshipDeliveryFrequency',
    'GetAutoOrderDetails',
  ],
  endpoints: builder => ({
    getCustomerEzShips: builder.query<EzShipsResponse, void>({
      query: () => ({
        url: '/GetCustomerAutoOrders',
        next: { tags: ['CustomerEzShips'] },
      }),
      transformResponse: (response: EzShipsResponse, meta, arg) => getCustomerEzShipsAdapter(response),
      transformErrorResponse: response => response as ResponseError,
      providesTags: ['CustomerEzShips'],
    }),
    getCustomerAutoOrderList: builder.query<SuccessResponse<Array<EzShipType>>, void>({
      query: () => ({
        url: '/GetCustomerAutoOrderList',
        next: { tags: ['GetCustomerAutoOrderList'] },
      }),
      transformResponse: (response: SuccessResponse<Array<EzShipType>>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      providesTags: ['GetCustomerAutoOrderList'],
    }),

    addToExistingEzShip: builder.mutation<SuccessResponse<unknown>, AddToExistingEzShipPayload>({
      query: data => ({
        url: '/AddItemToAutoOrder',
        method: 'POST',
        body: JSON.stringify(data),
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
    }),

    addToEzShip: builder.mutation<AddToEzShipResponse, AddToEzshipPayload>({
      query: data => ({
        url: '/AddItemToAutoOrder',
        method: 'POST',
        body: JSON.stringify(data),
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      invalidatesTags: (result, error, arg) => (!error && result?.success ? ['EzShipCartItems'] : []),
      transformResponse: (response: AddToEzShipResponse, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      async onQueryStarted(addToEzShipPayload, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        const { autoOrderID } = (data.data || {}) as { autoOrderID: number };
        if (!autoOrderID || addToEzShipPayload.autoOrderID == autoOrderID) return;
        dispatch(setEzShipConfig({ isNewEzShip: true, ezShipId: autoOrderID }));
      },
    }),

    getEzShipCartItems: builder.query<EzShipCartResp, { autoOrderId: string }>({
      query: data => ({
        url: `/GetEzShipCartItems?${new URLSearchParams(data).toString()}`,
      }),
      providesTags: ['EzShipCartItems'],

      async transformResponse(baseQueryReturnValue: EzShipCartResp, meta, arg) {
        await store.dispatch(setCurrentEzShip(baseQueryReturnValue));
        return baseQueryReturnValue;
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        dispatch(setGetEzShipCartItemsStatus(true));
        try {
          await queryFulfilled;
        } catch (ex) {
        } finally {
          dispatch(setGetEzShipCartItemsStatus(false));
          dispatch(removeAction());
        }
      },
    }),

    removeItemFromEzShipCart: builder.mutation<SuccessResponse<unknown>, { itemCode: string; autoOrderId: string }>({
      query: data => ({
        url: `/RemoveItem`,
        method: 'POST',
        body: JSON.stringify(data),
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      invalidatesTags: (result, error, arg) => (!error && result?.success ? ['EzShipCartItems'] : []),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        dispatch(setRemoveEzShipCartItemStatus(true));
        try {
          await queryFulfilled;
        } catch (ex) {
        } finally {
          dispatch(setRemoveEzShipCartItemStatus(false));
        }
      },
    }),

    saveShippingAddress: builder.mutation<SuccessResponse<unknown>, { autoOrderID: string } & Address>({
      // query: ({ autoOrderID, ...rest }) => (),
      queryFn: async ({ autoOrderID, ...rest }, api, extraOptions, baseQuery) => {
        const resp = await baseQuery({
          url: `/SaveShippingAddress?autoOrderID=${autoOrderID}`,
          method: 'POST',
          body: rest,
          headers: {
            'Content-Type': contentTypes.applicationJson,
          },
        });

        // if (!IS_PROD) await sleep(ADDRESS_UPDATION_WAIT_TIME);

        if (resp.error) return { error: resp.error };

        return resp as any;
      },

      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(addressApi.util.invalidateTags(['GetCustomerAddresses']));
      },
    }),

    submitEzShip: builder.mutation<
      SuccessResponse<unknown>,
      {
        autoOrderID: string;
        frequencyType: string;
        specificDayInterval?: string;
        startDate: string;
        addressType: string;
        creditCardType: string;
      }
    >({
      query: data => ({
        url: `/SaveDelieveryPreferenceEzshipCart?${new URLSearchParams(data).toString()}`,
        method: 'POST',
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
    }),

    addFoodLabelToEzShipItem: builder.mutation<
      SuccessResponse<unknown>,
      {
        autoOrderID: string;
        labelID: number;
        itemCode: string;
        petSpecieID: number;
        isActive: boolean;
      }
    >({
      query: data => ({
        url: `/AddEZShipLabels`,
        method: 'POST',
        body: data,
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      invalidatesTags: ['EzShipCartItems'],
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
    }),

    removeLabelFromEzShipItem: builder.mutation<
      SuccessResponse<unknown>,
      {
        autoOrderID: number;
        itemCode: number;
        recordnumber: number;
      }
    >({
      query: data => ({
        url: `/RemoveLabel?${new URLSearchParams(data as unknown as Record<string, string>).toString()}`,
        method: 'POST',
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      invalidatesTags: ['EzShipCartItems'],
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        dispatch(setFoodLabelRemovalStatus(true));
        await queryFulfilled;
        dispatch(setFoodLabelRemovalStatus(false));
      },
    }),

    getEzShipTitle: builder.query<SuccessResponse<string>, { autoOrderId: string }>({
      query: ({ autoOrderId }) => ({
        url: `/GetEzShipTitle?autoOrderID=${autoOrderId}`,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        const resp = await queryFulfilled;
        console.log('setting ezship title', resp);
        dispatch(setEzShipTitle(resp.data.data));
      },
    }),

    getEzshipAutoOrdersHistory: builder.query<SuccessResponse<EzshipManagerAutoOrderHistory>, { autoOrderId?: string }>(
      {
        query: ({ autoOrderId = '' }) => ({
          url: '/GetEZShipHistory',
          params: { AutoorderID: autoOrderId },
        }),
        transformResponse: (response: SuccessResponse<EzshipManagerAutoOrderHistory>, meta, arg) => response,
        transformErrorResponse: response => response as ResponseError,
        providesTags: ['GetEzshipOrdersHistory'],
      }
    ),
    updateEzshipDeliveryFrequency: builder.mutation<SuccessResponse<unknown>, UpdateEzshipDeliveryPreference>({
      query: payload => ({
        url: `/UpdateDeliveryFrequency`,
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: (result, error, arg) =>
        !error && result?.success ? ['GetCustomerAutoOrderList', 'GetAutoOrderDetails'] : [],
    }),
    createEzShipTitle: builder.mutation<SuccessResponse<unknown>, { title: string }>({
      query: payload => ({
        url: '/SaveTitle',
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        const resp = await queryFulfilled;
        console.log('setting ezship title', resp);
        dispatch(setEzShipTitle(_.title));
      },
    }),
    getAutoOrderDetails: builder.query<EzshipAutoOrderDetailResponse, string>({
      query: autoOrderId => ({
        url: `/ezshipdetail/${autoOrderId}`,
      }),
      transformResponse: (response: EzshipAutoOrderDetailResponse, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      providesTags: ['GetAutoOrderDetails'],
    }),
    updateAutoOrderEzshipName: builder.mutation<SuccessResponse<unknown>, { autoOrderID: string; title: string }>({
      query: payload => ({
        url: '/UpdateTitle',
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: (result, error, arg) => (!error && result?.success ? ['GetAutoOrderDetails'] : []),
    }),
    processNowEzshipAutoOrder: builder.mutation<SuccessResponse<unknown>, { autoOrderId: string }>({
      query: ({ autoOrderId }) => ({
        url: `/ProcessAutoOrder/?autoOrderID=${autoOrderId}`,
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: ['GetAutoOrderDetails'],
    }),
    skipProductInAutoOrderProductsList: builder.mutation<SuccessResponse<unknown>, SkipProductInAutoOrderPayload>({
      query: payload => ({
        url: '/SkipCycle',
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: ['GetAutoOrderDetails'],
    }),
    removeProductInAutoOrderProductsList: builder.mutation<SuccessResponse<unknown>, SkipProductInAutoOrderPayload>({
      query: payload => ({
        url: '/RemoveItem',
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: ['GetAutoOrderDetails'],
    }),
    updateProductQuantityInAutoOrderProductsList: builder.mutation<
      SuccessResponse<unknown>,
      UpdateProductQuantityInAutoOrderPayload
    >({
      query: payload => ({
        url: '/UpdateItemQuantityEZship',
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: (result, error, arg) =>
        !error && result?.success ? ['EzShipCartItems', 'GetAutoOrderDetails'] : [],
    }),
    updateContactDetails: builder.mutation<SuccessResponse<unknown>, CustomerContactDetailsPayload>({
      query: payload => ({
        url: '/UpdateContactAutoOrder',
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: ['GetAutoOrderDetails'],
    }),
    activateEzshipAutoOrder: builder.mutation<SuccessResponse<unknown>, { autoOrderId: string; activate: boolean }>({
      query: ({ autoOrderId, activate }) => ({
        url: `/ActivateAutoOrder?autoOrderID=${autoOrderId}&activate=${activate}`,
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: ['GetAutoOrderDetails'],
    }),
    // updateCustomerAddressDetails: builder.mutation<SuccessResponse<unknown>, CustomerShippingAddressDetailsPayload>({
    //   query: payload => ({
    //     url: `/SaveShippingAddress?autoOrderID=${payload.autoOrderId}`,
    //     method: 'POST',
    //     body: JSON.stringify(payload),
    //     headers: {
    //       'Content-Type': contentTypes.applicationJson,
    //     },
    //   }),
    //   transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
    //   transformErrorResponse: response => response as ResponseError,
    //   invalidatesTags: ['GetAutoOrderDetails'],
    // }),
    updateCustomerAddressDetails: builder.mutation<SuccessResponse<unknown>, CustomerShippingAddressDetailsPayload>({
      queryFn: async ({ autoOrderId, ...rest }, api, extraOptions, baseQuery) => {
        const resp = await baseQuery({
          url: `/SaveShippingAddress?autoOrderID=${autoOrderId}`,
          method: 'POST',
          body: rest,
          headers: {
            'Content-Type': contentTypes.applicationJson,
          },
        });

        // await sleep(ADDRESS_UPDATION_WAIT_TIME);

        if (resp.error) return { error: resp.error };

        return resp as any;
      },

      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(ezShipApi.util.invalidateTags(['GetAutoOrderDetails']));
      },
    }),
    deleteEzship: builder.mutation<SuccessResponse<unknown>, { autoOrderId: string }>({
      query: ({ autoOrderId }) => ({
        url: `/DeleteAutoOrder?autoOrderId=${autoOrderId}`,
        method: 'POST',
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    removeCoupon: builder.mutation<SuccessResponse<unknown>, { autoOrderId: string }>({
      query: ({ autoOrderId }) => ({
        url: `/RemoveCouponCode/${autoOrderId}`,
        method: 'GET',
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: ['GetAutoOrderDetails'],
    }),
    addCoupon: builder.mutation<SuccessResponse<unknown>, { autoOrderId: string; couponCode: string }>({
      query: payload => ({
        url: '/ApplyCouponCode/ApplyCouponCode',
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: ['GetAutoOrderDetails'],
    }),

    savePaymentMethod: builder.mutation<
      SuccessResponse<unknown>,
      {
        autoOrderID: number | string;
        creditCardType: number | string;
      }
    >({
      query: payload => ({
        url: `/SavePaymentMethod`,
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: ['GetAutoOrderDetails'],
    }),
    usePawPay: builder.mutation<SuccessResponse<unknown>, number | string>({
      query: autoOrderID => ({
        url: `/UsePawPay?autoOrderID=${autoOrderID}`,
        method: 'GET',
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: ['GetAutoOrderDetails'],
    }),

    addCreditCardWithPawPay: builder.mutation<
      SuccessResponse<unknown>,
      { payload: AddCreditCardPayload; billingSameAsShipping: boolean; autoOrderId?: string | number }
    >({
      query: ({ autoOrderId, payload }) => ({
        url: `/UsePawPayAndSaveCard?autoOrderID=${autoOrderId}`,
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: ['GetAutoOrderDetails', 'EzShipCartItems'],
    }),
    selectAddress: builder.mutation<
      SuccessResponse<unknown>,
      { autoOrderId?: string | number; addressType: number | string }
    >({
      query: ({ autoOrderId, addressType }) => ({
        url: `/SaveShippingAddressType?autoOrderID=${autoOrderId}&addressType=${addressType}`,
        method: 'POST',
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: ['GetAutoOrderDetails'],
    }),
  }),
});

export const {
  useGetCustomerEzShipsQuery,
  useAddToExistingEzShipMutation,
  useAddToEzShipMutation,
  useGetEzShipCartItemsQuery,
  useRemoveItemFromEzShipCartMutation,
  useSaveShippingAddressMutation,
  useSubmitEzShipMutation,
  useAddFoodLabelToEzShipItemMutation,
  useRemoveLabelFromEzShipItemMutation,
  useGetCustomerAutoOrderListQuery,
  useUpdateEzshipDeliveryFrequencyMutation,
  useGetEzshipAutoOrdersHistoryQuery,
  useCreateEzShipTitleMutation,
  useGetAutoOrderDetailsQuery,
  useUpdateAutoOrderEzshipNameMutation,
  useProcessNowEzshipAutoOrderMutation,
  useSkipProductInAutoOrderProductsListMutation,
  useRemoveProductInAutoOrderProductsListMutation,
  useUpdateProductQuantityInAutoOrderProductsListMutation,
  useUpdateContactDetailsMutation,
  useActivateEzshipAutoOrderMutation,
  useUpdateCustomerAddressDetailsMutation,
  useDeleteEzshipMutation,
  useAddCouponMutation,
  useRemoveCouponMutation,
  useUsePawPayMutation,
  useSavePaymentMethodMutation,
  useAddCreditCardWithPawPayMutation,
  useSelectAddressMutation,
  useGetEzShipTitleQuery,
} = ezShipApi;
